import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import PermissionsContainer from '../../components/PermissionsContainer';
import { usePermissionsMode } from '../../contexts/PermissionsModeContext';
import SingleSelect from '../../components/SingleSelect';
import safetySystemService from '../../services/safetySystem.service';
import { config } from '../../config/config';
import type { DriverSummary, ExpirationItem, Expirations, RecordsBuckets, SafetyRegion } from '../../types/safetySystem';

/** Port of the V1 Safety main menu (menus_safety.pl): driver summary, records, expirations. */

/** V1 CMS "HAZMAT PROS needing approval" saved search (menus_safety.pl:604). */
const HAZMAT_APPROVAL_URL =
  `${config.LEGACY_CMS_URL}/index_cms.pl?action=savesearch&searchtype=hazmatapprovedby&searchval=&searchactive=HAZMATAPPROVAL&searchdisplay=hazmatapproval`;

const unitsUrl = (params: Record<string, string>) => `/safety/units?${new URLSearchParams(params).toString()}`;

const dateLabel = (value: string) => {
  const [year, month, day] = value.slice(0, 10).split('-');
  return year && month && day ? `${month}/${day}/${year}` : value;
};

/** Field → label for the "missing dates" list (mirrors dashboard.py _EXP_FIELDS). */
const EXP_LABELS: Record<string, string> = {
  birthday: 'Birthday',
  dotinspecexpdate: 'DOT Inspection',
  registrationexpdate: 'Truck Registration',
  driverlicexpdate: "Driver's License",
  medcardexpdate: 'Medical Card',
};

const num = (value: number | null | undefined) => (value === null || value === undefined ? '—' : value.toLocaleString());

interface RowProps {
  label: string;
  value: number | null | undefined;
  onClick?: () => void;
  indent?: boolean;
  tone?: 'danger' | 'accent';
}

function Row({ label, value, onClick, indent, tone }: RowProps) {
  const valueClass = tone === 'danger' ? 'text-danger-fg' : tone === 'accent' ? 'text-accent-text' : 'text-heading';
  const inner = (
    <>
      <span className={`${indent ? 'pl-5' : ''} text-sm`}>{label}</span>
      <span className={`text-sm font-semibold tabular-nums ${valueClass}`}>{num(value)}</span>
    </>
  );
  if (!onClick) {
    return <div className="flex items-center justify-between gap-3 px-3 py-1.5">{inner}</div>;
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center justify-between gap-3 rounded px-3 py-1.5 text-left hover:bg-highlight focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
    >
      {inner}
    </button>
  );
}

function Card({ title, children, actions }: { title: string; children: ReactNode; actions?: ReactNode }) {
  return (
    <section
      aria-label={title}
      className="flex min-h-0 flex-col rounded-lg border border-rule bg-white shadow-sm dark:bg-gray-900"
    >
      <header className="flex items-center justify-between gap-2 border-b border-rule px-3 py-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-heading">{title}</h2>
        {actions}
      </header>
      <div className="flex flex-col py-1">{children}</div>
    </section>
  );
}

export default function SafetyHub() {
  const navigate = useNavigate();
  const { setPageComponents } = usePermissionsMode();

  const pageComponentsList = useMemo(() => [{ id: 'safety-system-hub', name: 'Safety Hub' }], []);
  useEffect(() => {
    setPageComponents(pageComponentsList);
    return () => setPageComponents([]);
  }, [pageComponentsList, setPageComponents]);

  const [region, setRegion] = useState('ALL');
  const [regions, setRegions] = useState<SafetyRegion[]>([]);
  const [summary, setSummary] = useState<DriverSummary | null>(null);
  const [records, setRecords] = useState<RecordsBuckets | null>(null);
  const [expirations, setExpirations] = useState<Expirations | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Regions only need to load once.
  useEffect(() => {
    const controller = new AbortController();
    safetySystemService.regions(controller.signal)
      .then((rows) => { if (!controller.signal.aborted) setRegions(rows); })
      .catch(() => { /* region filter degrades to ALL */ });
    return () => controller.abort();
  }, []);

  const refresh = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError('');
    const results = await Promise.allSettled([
      safetySystemService.driverSummary(region, signal),
      safetySystemService.records(signal),
      safetySystemService.expirations(signal),
    ]);
    if (signal?.aborted) return;
    const [s, r, e] = results;
    setSummary(s.status === 'fulfilled' ? s.value : null);
    setRecords(r.status === 'fulfilled' ? r.value : null);
    setExpirations(e.status === 'fulfilled' ? e.value : null);
    if (results.some((x) => x.status === 'rejected')) {
      setError('Some Safety Hub sections could not be loaded. Retry or check your access with an administrator.');
    }
    setLoading(false);
  }, [region]);

  useEffect(() => {
    const controller = new AbortController();
    void refresh(controller.signal);
    return () => controller.abort();
  }, [refresh]);

  const regionOptions = useMemo(
    () => [{ value: 'ALL', label: 'All regions' }, ...regions.map((r) => ({ value: String(r.recno), label: r.label }))],
    [regions],
  );

  const goUnits = (params: Record<string, string>) => navigate(unitsUrl(params));
  const driverBucket = (bucket: string) => goUnits({ view: 'driver', bucket, region });
  const driverStatus = (status: string) => goUnits({ view: 'driver', status, region });

  const renderExpiration = (item: ExpirationItem, keyPrefix: string) => {
    if (item.units.length >= 3) {
      return (
        <button
          key={`${keyPrefix}-${item.date}-${item.field}`}
          type="button"
          onClick={() => goUnits({ view: 'driver', q: item.units.join(','), search_by: 'unitlist' })}
          className="flex w-full items-center justify-between gap-3 rounded px-3 py-1 text-left text-sm hover:bg-highlight"
        >
          <span className="text-muted tabular-nums">{dateLabel(item.date)}</span>
          <span className="flex-1 text-heading">{`${item.label} (${item.units.length})`}</span>
        </button>
      );
    }
    return item.units.map((unit) => (
      <button
        key={`${keyPrefix}-${item.date}-${item.field}-${unit}`}
        type="button"
        onClick={() => goUnits({ open: unit })}
        className="flex w-full items-center justify-between gap-3 rounded px-3 py-1 text-left text-sm hover:bg-highlight"
      >
        <span className="text-muted tabular-nums">{dateLabel(item.date)}</span>
        <span className="flex-1 text-heading">{`${unit} - ${item.label}`}</span>
      </button>
    ));
  };

  return (
    <main className="p-2 sm:p-4 flex flex-col gap-3 text-gray-900 dark:text-gray-100">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Safety Hub</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">Driver summary, open records, and upcoming expirations. Click a row to open the matching units.</p>
        </div>
        <button
          type="button"
          onClick={() => void refresh()}
          disabled={loading}
          className="inline-flex items-center gap-1.5 rounded-md border border-rule px-3 py-1.5 text-sm hover:bg-highlight disabled:cursor-not-allowed disabled:text-disabled"
        >
          <ArrowPathIcon width={16} aria-hidden="true" />
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </header>
      {error && <div role="alert" className="rounded-md border border-danger bg-danger px-3 py-2 text-sm text-danger-fg">{error}</div>}

      <PermissionsContainer componentId="safety-system-hub" componentName="Safety Hub" defaultHidden>
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
          <Card
            title="Driver Summary"
            actions={
              <SingleSelect ariaLabel="Region" value={region} onChange={setRegion} options={regionOptions}
                size="sm" className="w-40" enableSearch usePortal />
            }
          >
            <Row label="ACTIVE DRIVERS" value={summary?.active} onClick={() => driverStatus('ALL')} />
            <Row label={`IN SERVICE (${summary?.inservice_pct ?? 0}%)`} value={summary?.inservice} tone="accent"
              onClick={() => driverStatus('IN SERVICE')} />
            <Row label={`OUT OF SERVICE (${summary?.outofservice_pct ?? 0}%)`} value={summary?.outofservice} tone="danger"
              onClick={() => driverStatus('OUT OF SERVICE')} />
            {(summary?.oos_by_type ?? []).map((t) => (
              <Row key={t.type} label={t.type} value={t.count} indent onClick={() => driverBucket(`oos_type:${t.type}`)} />
            ))}
            <Row label="MULE DRIVERS" value={summary?.muledriver} onClick={() => driverBucket('muledriver')} />
            <div className="my-1 border-t border-rule" />
            <Row label="DISPATCHED TODAY" value={summary?.dispatched} onClick={() => driverBucket('dispatched')} />
            <Row label="LOCAL" value={summary?.dispatched_local} indent />
            <Row label="OTR" value={summary?.dispatched_otr} indent />
            <Row label="NOT DISPATCHED TODAY" value={summary?.notdispatched} onClick={() => driverBucket('notdispatched')} />
            <Row label="LOCAL" value={summary?.notdispatched_local} indent />
            <Row label="OTR" value={summary?.notdispatched_otr} indent />
            <Row label="NOT WORKING" value={summary?.notworking} indent />
            <Row label="UNKNOWN" value={summary?.unknown} indent />
          </Card>

          <Card title="Records">
            <Row label="OPEN TICKETS" value={records?.open_tickets} onClick={() => navigate('/safety/tickets?open=1')} />
            <Row label="OPEN MAINTENANCE REPORTS" value={records?.open_maint_reports} onClick={() => navigate('/safety/maintenance-reports?open=1')} />
            <Row label="OPEN INCIDENTS" value={records?.open_incidents} onClick={() => navigate('/safety/incidents?open=1')} />
            <div className="my-1 border-t border-rule" />
            <Row label="DRIVERLESS TRUCKS" value={records?.driverless_trucks}
              onClick={() => goUnits({ view: 'truck', bucket: 'driverless', region: 'ALL' })} />
            <Row label="TRUCKLESS MULE DRIVERS" value={records?.truckless_mule_drivers}
              onClick={() => goUnits({ view: 'driver', bucket: 'truckless_mule', region: 'ALL' })} />
            <Row label="TRUCKLESS W2 / TEMP DRIVERS" value={records?.truckless_w2_temp_drivers}
              onClick={() => goUnits({ view: 'driver', bucket: 'truckless_w2temp', region: 'ALL' })} />
            <div className="my-1 border-t border-rule" />
            <Row label="HAZMAT PROS" value={records?.hazmat_pros}
              onClick={() => window.open(HAZMAT_APPROVAL_URL, '_blank', 'noopener,noreferrer')} />
          </Card>

          <Card title="Expirations">
            <h3 className="px-3 pt-1 text-xs font-semibold uppercase text-danger-fg">Past due</h3>
            {expirations && expirations.past_due.length === 0 && <p className="px-3 py-1 text-sm text-muted">Nothing past due.</p>}
            {(expirations?.past_due ?? []).map((item) => renderExpiration(item, 'past'))}
            <h3 className="px-3 pt-3 text-xs font-semibold uppercase text-heading">Next 7 days</h3>
            {expirations && expirations.upcoming.length === 0 && <p className="px-3 py-1 text-sm text-muted">Nothing upcoming.</p>}
            {(expirations?.upcoming ?? []).map((item) => renderExpiration(item, 'up'))}
            {expirations && expirations.empty.length > 0 && (
              <>
                <h3 className="px-3 pt-3 text-xs font-semibold uppercase text-muted">Missing dates</h3>
                {expirations.empty.map((e) => (
                  <button
                    key={e.field}
                    type="button"
                    onClick={() => goUnits({ view: 'driver', q: e.units.join(','), search_by: 'unitlist' })}
                    className="flex w-full items-center justify-between gap-3 rounded px-3 py-1 text-left text-sm hover:bg-highlight"
                  >
                    <span className="text-heading">{EXP_LABELS[e.field] ?? e.field}</span>
                    <span className="font-semibold tabular-nums">{e.units.length}</span>
                  </button>
                ))}
              </>
            )}
          </Card>
        </div>
      </PermissionsContainer>
    </main>
  );
}
